import { HttpParams } from '@angular/common/http';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { IYearDto } from '@models/year-dto';
import { GenericEnumType } from './array-enum-generic-type';

export function url(...args: any[]) {
  return args.join('/');
}

export function getObjectValueByKey(obj: any, key: string): any {
  if (!obj || typeof obj !== 'object') return undefined;

  if (key in obj) return obj[key];

  for (const value of Object.values(obj)) {
    const result = getObjectValueByKey(value, key);
    if (result !== undefined) return result;
  }

  return undefined;
}

export function toUrl(...args: (string | number)[]): string {
  let path = '';

  for (let i = 0; i < args.length; i++) {
    if (i > 0) path += '/';
    path += args[i];
  }

  return path;
}

export function toParams(...args: any[]): HttpParams {
  let params = new HttpParams();

  args.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          Object.keys(value).forEach((subKey) => {
            const subValue = value[subKey];
            if (subValue !== undefined && subValue !== null) {
              params = params.append(subKey, subValue);
            }
          });
        } else {
          params = params.append(key, value);
        }
      }
    });
  });

  return params;
}

export function filterEnumForKeyValuePipe<T extends { [key: string]: string }>(
  enumObj: T,
  includedValues: T[keyof T][],
): { [key in keyof T]?: T[key] } {
  return Object.entries(enumObj)
    .filter(([key, value]) => includedValues.includes(value as T[keyof T]))
    .reduce(
      (acc, [key, value]) => {
        acc[key as keyof T] = value as T[keyof T];
        return acc;
      },
      {} as { [key in keyof T]?: T[key] },
    );
}

export function calculateDisplayTime(message: string): number {
  const baseTime = 3000;
  const timePerChar = 70;
  const maxTime = 30000;

  const displayTime = baseTime + message.length * timePerChar;
  return Math.min(displayTime, maxTime);
}

export function formatEnumLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w|\s\w/g, (match) => match.toUpperCase());
}

export function getAppBaseUrl(document: Document): string {
  const origin = window.location.origin;

  const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
  return `${origin}${baseHref}`;
}
export function normalizeUrl(url: string): string {
  return url.startsWith('/') ? url.slice(1) : url;
}

export function openNewTab(
  paths: unknown[],
  router: Router,
  document: Document,
): void {
  const url = router.serializeUrl(router.createUrlTree([...paths]));

  let appBaseUrl = getAppBaseUrl(document);

  if (environment.versionTag === 'DEV' && !appBaseUrl.includes('/dev/')) {
    appBaseUrl = `${window.location.origin}/dev/`;
  }

  window.open(`${appBaseUrl}${normalizeUrl(url)}`, '_blank');
}

export function enumToDropdownOptions<T extends object>(
  enumObj: T,
): GenericEnumType[] {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: key,
    value: value,
  }));
}

export function enumValueToDropdownOption<
  T extends Record<string, string | number>,
>(
  enumObj: T,
  value: T[keyof T] | string | undefined,
): { label: string; value: string } {
  if (typeof value === 'string') {
    value = enumObj[value as keyof T] as T[keyof T];
  }

  if (value === undefined || value === null)
    return {} as { label: string; value: string };

  const entry = Object.entries(enumObj).find(([, val]) => val === value);

  if (!entry) return {} as { label: string; value: string };

  const [key, val] = entry;
  return {
    label: key,
    value: val.toString(), // garante string
  };
}

export function formatDateToBR(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return ''; // retorna vazio se a data for inválida

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export function addError(control: AbstractControl | null, key: string) {
  if (!control) return;

  const currentErrors = control.errors || {};
  currentErrors[key] = true;
  control.setErrors(currentErrors);
}

export function removeError(control: AbstractControl | null, key: string) {
  if (!control) return;

  const currentErrors = control.errors;
  if (!currentErrors) return;

  delete currentErrors[key];

  if (Object.keys(currentErrors).length === 0) {
    control.setErrors(null);
  } else {
    control.setErrors(currentErrors);
  }
}

export function yearsArrayBuilder(years: number[]): IYearDto[] {
  const y = years.map((year) => ({ key: year.toString(), value: year }));
  y.sort((a, b) => b.value - a.value);
  return y;
}

export function trimmedMinLengthValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const actualLength = value.trim().length;
    if (actualLength < minLength) {
      return {
        min: true,
      };
    }
    return null;
  };
}
